import React, {useEffect, useState} from 'react';
import Routing from "./routing/routing";
import Cookies from "js-cookie";
import keycloak from "./keycloak";
import {BasketProvider} from "./providers/basket-context";
import {UserProvider} from "./providers/user-context";
import {createBrowserRouter, RouterProvider} from "react-router-dom";
import LogoLoader from "./components/common/logo-loader";
import {Alert, Box, Button} from "@mui/material";
import {useTranslation} from "react-i18next";
import {NotificationProvider} from "./notifications/notification-context";

// Keycloak allows initialization only once, including StrictMode effect replay.
let initialization;

// A data router supports navigation blockers for unsaved profile edits, including browser Back.
const router = createBrowserRouter([{
    path: "*", element: <NotificationProvider><Routing /></NotificationProvider>,
}]);

function App() {

    const [authenticated, setAuthenticated] = useState(false);
    const [keycloakInitialized, setKeycloakInitialized] = useState(false);
    const {t} = useTranslation();

    useEffect(() => {
        let active = true;
        initialization ||= Promise.resolve().then(() => keycloak.init({onLoad: 'login-required'}));
        initialization
            .then(auth => {
                if (!active) return;
                setAuthenticated(auth);
                setKeycloakInitialized(true);

                // Store only the non-sensitive tenantId in a cookie (needed server-side).
                // The access token is read directly from keycloak.token in axios-config
                // so it never needs to be persisted in a cookie.
                const tenantId = keycloak.tokenParsed?.platform_tenantId;
                if (tenantId) Cookies.set("currentTenantId", tenantId, {
                    secure: true,
                    sameSite: "Strict",
                });
            })
            .catch(error => {
                if (!active) return;
                console.error('Keycloak initialization failed', error);
                setKeycloakInitialized(true);
            });
        return () => { active = false; };
    }, []);

    if (!keycloakInitialized) {
        return <LogoLoader size={80} showText={true} />;
    }

    if (!authenticated) {
        return (
            <Box sx={{maxWidth: 480, mx: "auto", p: 3, mt: 8}}>
                <Alert severity="error">{t("LoginFailed", "Unable to sign in. Please try again.")}</Alert>
                <Button sx={{mt: 2}} variant="contained" onClick={() => window.location.reload()}>
                    {t("Retry", "Retry")}
                </Button>
            </Box>
        );
    }

    const type = keycloak.tokenParsed?.platform_tenantType;
    const name = keycloak.tokenParsed?.platform_tenantName;
    const email = keycloak.tokenParsed?.email;
    const tenantId = keycloak.tokenParsed?.platform_tenantId;

    return (
        <UserProvider userFullName={email} username={keycloak.tokenParsed?.preferred_username || email}
                      tenantName={name} tenantType={type} tenantId={tenantId}>
            <BasketProvider>
                <RouterProvider router={router}/>
            </BasketProvider>
        </UserProvider>
    );
}

export default App;
